/**
 * Privilege Checking Utilities
 * 
 * Functions to check if a user has required privileges from their admin assignments.
 */

import type { User } from "@/services/authService";
import type { AdminPrivilege } from "@/services/adminService";

export type { AdminPrivilege };

/** Legacy DB `userLevel` from API (number or numeric string). */
export function getNumericUserLevel(user: User | null | undefined): number {
  const n = Number(user?.userLevel);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Scope stage from active `adminAssignments.level` only.
 * 3 = nasional, 2 = province, 1 = city, 0 = none.
 */
export type UserStage = 0 | 1 | 2 | 3;

type AdminScopeLevel = "nasional" | "province" | "city";

function maxAdminScopeFromAssignments(user: User | null | undefined): AdminScopeLevel | undefined {
  if (!user?.adminAssignments?.length) return undefined;
  const active = user.adminAssignments.filter((a) => a.isActive);
  const levels = active.map((a) => a.level);
  if (levels.includes("nasional")) return "nasional";
  if (levels.includes("province")) return "province";
  if (levels.includes("city")) return "city";
  return undefined;
}

function stageFromAdminScope(level: AdminScopeLevel | undefined): UserStage {
  if (level === "nasional") return 3;
  if (level === "province") return 2;
  if (level === "city") return 1;
  return 0;
}

/** Prefer `user.userStage` from API; otherwise derive from `adminAssignments.level` only. */
export function getNumericUserStage(user: User | null | undefined): UserStage {
  const raw = Number(user?.userStage);
  if (Number.isFinite(raw) && raw >= 0 && raw <= 3) {
    return raw as UserStage;
  }
  return stageFromAdminScope(maxAdminScopeFromAssignments(user));
}

/** Legacy userLevel 3 only: full privilege bypass (not assignment-only nasional). */
export function hasSuperAdminUserLevel(user: User | null | undefined): boolean {
  return getNumericUserLevel(user) === 3;
}

type NasionalAssignmentShape = {
  level?: string;
  isActive?: boolean;
  privileges?: AdminPrivilege[] | null;
};

/**
 * Whether this active nasional assignment counts for UI/location scope: privileges required unless superadmin.
 */
export function countsAsEffectiveNasionalAssignment(
  user: User | null | undefined,
  assignment: NasionalAssignmentShape
): boolean {
  if (!assignment.isActive || assignment.level !== "nasional") return false;
  if (hasSuperAdminUserLevel(user)) return true;
  const p = assignment.privileges;
  return Array.isArray(p) && p.length > 0;
}

/**
 * Nasional admin UI/scope: active nasional row must carry at least one privilege,
 * unless userLevel 3 (legacy superadmin).
 */
export function hasEffectiveNasionalAdminAssignment(user: User | null | undefined): boolean {
  if (!user?.adminAssignments?.length) return false;
  return user.adminAssignments.some((a) => countsAsEffectiveNasionalAssignment(user, a));
}

/**
 * Active nasional assignment exists but privileges are empty — non-superadmin misconfiguration (e.g. toast after login).
 */
export function hasNasionalAssignmentMissingPrivileges(user: User | null | undefined): boolean {
  if (!user?.adminAssignments?.length || hasSuperAdminUserLevel(user)) return false;
  return user.adminAssignments.some(
    (a) => a.isActive && a.level === "nasional" && !countsAsEffectiveNasionalAssignment(user, a)
  );
}

/**
 * Get all privileges from user's active admin assignments
 * @param user - User object with adminAssignments
 * @returns Array of all unique privileges from active assignments
 */
export function getUserPrivileges(user: User | null | undefined): AdminPrivilege[] {
  if (!user || !user.adminAssignments || user.adminAssignments.length === 0) {
    return [];
  }

  const privilegesSet = new Set<AdminPrivilege>();
  
  // Collect privileges from all active admin assignments
  user.adminAssignments.forEach((assignment) => {
    if (assignment.isActive && assignment.privileges) {
      assignment.privileges.forEach((privilege) => {
        privilegesSet.add(privilege);
      });
    }
  });

  return Array.from(privilegesSet);
}

/** Privilege on active admin assignments only (no `userLevel` 3 bypass). */
export function hasAssignmentPrivilege(
  user: User | null | undefined,
  privilege: AdminPrivilege
): boolean {
  return getUserPrivileges(user).includes(privilege);
}

/**
 * Check if user has a specific privilege
 * @param user - User object with adminAssignments
 * @param privilege - Privilege to check for
 * @returns true if user has the privilege, false otherwise
 */
export function hasPrivilege(
  user: User | null | undefined,
  privilege: AdminPrivilege
): boolean {
  if (hasSuperAdminUserLevel(user)) {
    return true;
  }
  const userPrivileges = getUserPrivileges(user);
  return userPrivileges.includes(privilege);
}

/**
 * Check if user has any of the required privileges
 * @param user - User object with adminAssignments
 * @param privileges - Array of privileges to check
 * @returns true if user has at least one of the privileges, false otherwise
 */
export function hasAnyPrivilege(
  user: User | null | undefined,
  privileges: AdminPrivilege[]
): boolean {
  if (privileges.length === 0) {
    return true; // No privileges required means accessible to all
  }

  if (hasSuperAdminUserLevel(user)) {
    return true;
  }

  const userPrivileges = getUserPrivileges(user);
  return privileges.some((privilege) => userPrivileges.includes(privilege));
}

/**
 * Check if user has all of the required privileges
 * @param user - User object with adminAssignments
 * @param privileges - Array of privileges to check
 * @returns true if user has all of the privileges, false otherwise
 */
export function hasAllPrivileges(
  user: User | null | undefined,
  privileges: AdminPrivilege[]
): boolean {
  if (privileges.length === 0) {
    return true; // No privileges required means accessible to all
  }

  if (hasSuperAdminUserLevel(user)) {
    return true;
  }

  const userPrivileges = getUserPrivileges(user);
  return privileges.every((privilege) => userPrivileges.includes(privilege));
}

/**
 * Menu & route /keuangan/rekening — hanya `create_transaction_bank_account` pada penugasan aktif
 * (bukan `manage_payments`; tanpa bypass userLevel 3 di UI — sama pola seperti hak anggota spesifik).
 */
export function canAccessKeuanganRekening(user: User | null | undefined): boolean {
  return hasAssignmentPrivilege(user, "create_transaction_bank_account");
}

/**
 * Menu & routes /keuangan/transfer-plans* — hanya `manage_transfer_plans` pada penugasan aktif.
 */
export function canAccessKeuanganTransferPlans(user: User | null | undefined): boolean {
  return hasAssignmentPrivilege(user, "manage_transfer_plans");
}

/**
 * Silent Key: `manage_silentkey` on an active admin assignment (no userLevel 3 bypass)
 * plus nasional scope (userStage 3). Legacy userLevel 3 only satisfies scope, not the privilege.
 */
export function canAccessSilentKeyAdmin(user: User | null | undefined): boolean {
  if (!user) return false;
  if (!hasAssignmentPrivilege(user, "manage_silentkey")) return false;
  return getNumericUserStage(user) === 3 || hasSuperAdminUserLevel(user);
}

